import { useLiveQuery } from 'dexie-react-hooks';
import type { SaleRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { businessLogoUrl } from '../../lib/images.ts';
import { useBalances } from '../customers/data.ts';
import { useMoney } from '../products/data.ts';
import { usePosSettings } from './state.ts';
import { printTicket, ticketText, whatsappLink, type TicketData } from './ticket.ts';

interface TicketOptions {
  /** Con el saldo de la cuenta corriente del cliente (el ticket recién hecha la venta). */
  withBalance?: boolean;
}

/** Imprimir o compartir el ticket de una venta, con los datos del negocio y la configuración. */
export function useTicket() {
  const { business, can } = useSession();
  const settings = usePosSettings();
  const money = useMoney();
  const members = useLiveQuery(() => db.memberships.where('business_id').equals(business.id).toArray(), [business.id]);
  const customers = useLiveQuery(() => db.customers.where('business_id').equals(business.id).toArray(), [business.id]);
  const balances = useBalances();

  // Saldo del cliente: si la venta fue (en parte) a cuenta corriente o si debe o tiene a favor. Solo
  // en el ticket recién hecha la venta: en una reimpresión sería el saldo de hoy, no el de ese momento.
  const customerBalance = (sale: SaleRow): number | null => {
    if (!sale.customer_id || !balances || !can('customer_accounts')) return null;
    const balance = balances.get(sale.customer_id) ?? 0;
    return balance !== 0 || sale.payments.some((p) => p.method === 'account') ? balance : null;
  };

  const data = (sale: SaleRow, originalNumber?: string | null, options?: TicketOptions): TicketData => ({
    business,
    sale,
    sellerName: members?.find((m) => m.user_id === sale.seller_id)?.display_name ?? null,
    footer: settings.ticket_footer,
    money,
    originalNumber,
    customerName: sale.customer_id ? (customers?.find((c) => c.id === sale.customer_id)?.name ?? null) : null,
    customerBalance: options?.withBalance ? customerBalance(sale) : null,
    logoUrl: settings.ticket_logo ? businessLogoUrl(business.logo_path) : null,
  });

  return {
    print: (sale: SaleRow, originalNumber?: string | null, options?: TicketOptions) =>
      printTicket(data(sale, originalNumber, options), settings.ticket_width),
    whatsapp: (sale: SaleRow, originalNumber?: string | null, options?: TicketOptions) =>
      window.open(whatsappLink(ticketText(data(sale, originalNumber, options))), '_blank', 'noopener'),
  };
}
